// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * All DOM construction and the chrome around the tool: the layout, the three
 * modals, the glossary tooltip and the toast. main.ts owns the state machine
 * and drives values into the handles this returns.
 */

import { GLOSSARY } from './glossary';

export interface AppRefs {
  // Input
  addrInput: HTMLInputElement;
  inspectBtn: HTMLButtonElement;
  suggestList: HTMLElement;
  suggestNote: HTMLElement;
  examples: HTMLElement;
  dropZone: HTMLElement;
  fileInput: HTMLInputElement;
  sampleBtn: HTMLButtonElement;
  progressWrap: HTMLElement;
  progressFill: HTMLElement;
  progressText: HTMLElement;
  errorBox: HTMLElement;
  // Repo file picker
  pickerPanel: HTMLElement;
  pickerList: HTMLElement;
  pickerTitle: HTMLElement;
  // Config
  configPanel: HTMLElement;
  vramSelect: HTMLSelectElement;
  vramCustom: HTMLInputElement;
  ctxSelect: HTMLSelectElement;
  cacheKSelect: HTMLSelectElement;
  cacheVSelect: HTMLSelectElement;
  nglInput: HTMLInputElement;
  nglAuto: HTMLInputElement;
  // Result
  verdictPanel: HTMLElement;
  modelName: HTMLElement;
  modelSub: HTMLElement;
  readBadge: HTMLElement;
  verdictWord: HTMLElement;
  verdictDetail: HTMLElement;
  verdictAdvice: HTMLElement;
  memBar: HTMLElement;
  memLegend: HTMLElement;
  tabs: HTMLElement;
  tabPanels: Record<string, HTMLElement>;
  breakdownBody: HTMLElement;
  quantBody: HTMLElement;
  metaBody: HTMLElement;
  templateBody: HTMLElement;
  reportCanvas: HTMLCanvasElement;
  cmdBlock: HTMLElement;
  ollamaBlock: HTMLElement;
  resultActions: HTMLElement;
  copyCmdBtn: HTMLButtonElement;
  copyOllamaBtn: HTMLButtonElement;
  dlJsonBtn: HTMLButtonElement;
  dlPngBtn: HTMLButtonElement;
  dlTemplateBtn: HTMLButtonElement;
  sharePngBtn: HTMLButtonElement;
  // Chrome
  drawer: HTMLElement;
  drawerBody: HTMLElement;
  drawerToggle: HTMLButtonElement;
  scrim: HTMLElement;
  banner: HTMLElement;
}

const ICON = {
  scope:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 21 21"/><path d="M7.5 8h6M7.5 11h4M7.5 14h5"/></svg>',
  search:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>',
  file:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>',
  sparkle:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/></svg>',
};

export function renderApp(root: HTMLElement): AppRefs {
  root.innerHTML = `
    <header class="topbar">
      <div class="brand">
        <span class="brand-mark">${ICON.scope}</span>
        <div class="brand-text">
          <span class="brand-name">GGUFScope</span>
          <span class="brand-tag">model size &amp; fit checker</span>
        </div>
      </div>
      <nav class="topnav">
        <button type="button" class="nav-btn" data-modal="how">How it works</button>
        <button type="button" class="nav-btn" data-modal="privacy">Privacy</button>
        <button type="button" class="nav-btn" data-modal="about">About</button>
        <button type="button" class="nav-btn drawer-toggle" id="drawer-toggle" aria-expanded="false">Event log</button>
      </nav>
    </header>

    <button type="button" class="trust-banner" id="trust-banner">
      Reads a few megabytes of a model's header over <span>HTTP Range</span> — never the weights. Local files never leave your device.
    </button>

    <main class="main-content">
      <div class="lede">
        <h1>Will this GGUF fit on your GPU?</h1>
        <p>
          Point it at a model. GGUFScope reads the real
          <span data-term="tensor">tensor table</span> out of the file itself — not the filename —
          and works out exactly what it needs in VRAM.
        </p>
      </div>

      <section class="panel" id="input-panel">
        <h2 class="panel-title">1 · Choose a model</h2>
        <div class="addr-row">
          <input
            class="addr-input"
            id="addr-input"
            type="text"
            spellcheck="false"
            autocapitalize="off"
            autocorrect="off"
            autocomplete="off"
            placeholder="Qwen/Qwen3-8B-GGUF  ·  or a full .gguf URL"
            aria-label="Hugging Face repo id or .gguf URL"
            role="combobox"
            aria-autocomplete="list"
            aria-controls="suggest-list"
            aria-expanded="false"
          />
          <button type="button" class="btn btn-primary" id="inspect-btn">${ICON.search}<span>Inspect</span></button>
        </div>
        <div class="suggest-list" id="suggest-list" role="listbox" aria-label="Matching Hugging Face GGUF repositories" hidden></div>
        <p class="suggest-note" id="suggest-note" role="status" hidden></p>
        <div class="examples" id="examples">
          <span class="examples-label">try:</span>
        </div>

        <div class="or-rule">or read a local file</div>

        <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Drop a .gguf file to inspect it locally">
          <div class="dz-icon">${ICON.file}</div>
          <div class="dz-text">
            <strong>Drop a .gguf file here</strong>
            <span>Only its header is read, in this tab. Nothing is uploaded — this path works fully offline.</span>
            <button type="button" class="sample-link" id="sample-btn">${ICON.sparkle}<span>Try a sample</span></button>
          </div>
          <input type="file" id="file-input" accept=".gguf,application/octet-stream" hidden />
        </div>

        <div class="progress-wrap" id="progress-wrap" hidden>
          <div class="progress-bar"><div class="progress-fill" id="progress-fill"></div></div>
          <div class="progress-text" id="progress-text"></div>
        </div>

        <div class="error-box" id="error-box" hidden></div>
      </section>

      <section class="panel" id="picker-panel" hidden>
        <h2 class="panel-title" id="picker-title">Pick a quantisation</h2>
        <div class="file-list" id="picker-list"></div>
      </section>

      <section class="panel" id="config-panel" hidden>
        <h2 class="panel-title">2 · Your hardware</h2>
        <div class="config-grid">
          <div class="field">
            <label for="vram-select">GPU memory</label>
            <select id="vram-select"></select>
          </div>
          <div class="field" id="vram-custom-field" hidden>
            <label for="vram-custom">GiB</label>
            <input type="number" id="vram-custom" min="1" max="1024" step="0.5" value="24" />
          </div>
          <div class="field">
            <label for="ctx-select"><span data-term="context length">context</span></label>
            <select id="ctx-select"></select>
          </div>
          <div class="field">
            <label for="cache-k-select">KV <span data-term="cache type">cache K</span></label>
            <select id="cache-k-select"></select>
          </div>
          <div class="field">
            <label for="cache-v-select">KV cache V</label>
            <select id="cache-v-select"></select>
          </div>
          <div class="field">
            <label for="ngl-input"><span data-term="offload">GPU layers</span></label>
            <input type="number" id="ngl-input" min="0" step="1" value="99" />
          </div>
          <div class="field field-check">
            <input type="checkbox" id="ngl-auto" checked />
            <label for="ngl-auto">Solve layers for me</label>
          </div>
        </div>
      </section>

      <section class="verdict" id="verdict-panel" hidden>
        <div class="model-head">
          <div>
            <div class="model-name" id="model-name"></div>
            <div class="model-sub" id="model-sub"></div>
          </div>
          <span class="read-badge" id="read-badge"></span>
        </div>

        <div class="verdict-headline">
          <span class="verdict-word" id="verdict-word"></span>
          <span class="verdict-detail" id="verdict-detail"></span>
        </div>
        <p class="verdict-advice" id="verdict-advice"></p>

        <div class="membar" id="membar"></div>
        <div class="membar-legend" id="mem-legend"></div>

        <div class="tabs" id="tabs" role="tablist" aria-label="Result detail">
          <button type="button" class="tab active" data-tab="breakdown" role="tab" aria-selected="true">Breakdown</button>
          <button type="button" class="tab" data-tab="quant" role="tab" aria-selected="false">Quant mix</button>
          <button type="button" class="tab" data-tab="command" role="tab" aria-selected="false">Command</button>
          <button type="button" class="tab" data-tab="meta" role="tab" aria-selected="false">Metadata</button>
          <button type="button" class="tab" data-tab="template" role="tab" aria-selected="false">Chat template</button>
          <button type="button" class="tab" data-tab="report" role="tab" aria-selected="false">Report card</button>
        </div>

        <div class="tab-panel" id="panel-breakdown">
          <div class="table-scroll"><table class="data" id="breakdown-table"><tbody id="breakdown-body"></tbody></table></div>
        </div>

        <div class="tab-panel" id="panel-quant" hidden>
          <div class="table-scroll"><table class="data"><tbody id="quant-body"></tbody></table></div>
          <p class="artefact-note">
            Every row is read from the file's own tensor table. A name like <code>Q4_K_M</code> is a
            <span data-term="mixture">mixture</span>, not a type — this is what is actually inside.
          </p>
        </div>

        <div class="tab-panel" id="panel-command" hidden>
          <div class="artefact-head">
            <span class="artefact-label">llama.cpp</span>
            <button type="button" class="btn btn-small" id="copy-cmd">Copy command</button>
          </div>
          <pre class="codeblock" id="cmd-block"></pre>
          <div class="artefact-head">
            <span class="artefact-label">Ollama Modelfile</span>
            <button type="button" class="btn btn-small" id="copy-ollama">Copy Modelfile</button>
          </div>
          <pre class="codeblock" id="ollama-block"></pre>
          <p class="artefact-note" id="ollama-note"></p>
        </div>

        <div class="tab-panel" id="panel-meta" hidden>
          <div class="table-scroll"><table class="data"><tbody id="meta-body"></tbody></table></div>
        </div>

        <div class="tab-panel" id="panel-template" hidden>
          <div class="artefact-head">
            <span class="artefact-label"><span data-term="chat template">chat template</span></span>
            <button type="button" class="btn btn-small" id="dl-template">Save .jinja</button>
          </div>
          <div id="template-body"></div>
        </div>

        <div class="tab-panel" id="panel-report" hidden>
          <canvas id="report-canvas" width="1200" height="630" aria-label="Shareable fit report card"></canvas>
        </div>

        <div class="result-actions" id="result-actions">
          <button type="button" class="btn btn-small" id="dl-json">Download fit-report.json</button>
          <button type="button" class="btn btn-small" id="dl-png">Save report PNG</button>
          <button type="button" class="btn btn-small" id="share-png" hidden>Share</button>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
      </div>
    </footer>

    <aside class="drawer" id="drawer" hidden aria-hidden="true"><div class="drawer-body" id="drawer-body"></div></aside>
    <div class="scrim" id="scrim" hidden></div>
    <div class="modal-host" id="modal-host" hidden></div>
    <div class="glossary-tip" id="glossary-tip" hidden></div>
    <div class="toast" id="toast" hidden></div>
  `;

  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;

  const refs: AppRefs = {
    addrInput: $<HTMLInputElement>('addr-input'),
    inspectBtn: $<HTMLButtonElement>('inspect-btn'),
    suggestList: $('suggest-list'),
    suggestNote: $('suggest-note'),
    examples: $('examples'),
    dropZone: $('dropzone'),
    fileInput: $<HTMLInputElement>('file-input'),
    sampleBtn: $<HTMLButtonElement>('sample-btn'),
    progressWrap: $('progress-wrap'),
    progressFill: $('progress-fill'),
    progressText: $('progress-text'),
    errorBox: $('error-box'),
    pickerPanel: $('picker-panel'),
    pickerList: $('picker-list'),
    pickerTitle: $('picker-title'),
    configPanel: $('config-panel'),
    vramSelect: $<HTMLSelectElement>('vram-select'),
    vramCustom: $<HTMLInputElement>('vram-custom'),
    ctxSelect: $<HTMLSelectElement>('ctx-select'),
    cacheKSelect: $<HTMLSelectElement>('cache-k-select'),
    cacheVSelect: $<HTMLSelectElement>('cache-v-select'),
    nglInput: $<HTMLInputElement>('ngl-input'),
    nglAuto: $<HTMLInputElement>('ngl-auto'),
    verdictPanel: $('verdict-panel'),
    modelName: $('model-name'),
    modelSub: $('model-sub'),
    readBadge: $('read-badge'),
    verdictWord: $('verdict-word'),
    verdictDetail: $('verdict-detail'),
    verdictAdvice: $('verdict-advice'),
    memBar: $('membar'),
    memLegend: $('mem-legend'),
    tabs: $('tabs'),
    tabPanels: {
      breakdown: $('panel-breakdown'),
      quant: $('panel-quant'),
      command: $('panel-command'),
      meta: $('panel-meta'),
      template: $('panel-template'),
      report: $('panel-report'),
    },
    breakdownBody: $('breakdown-body'),
    quantBody: $('quant-body'),
    metaBody: $('meta-body'),
    templateBody: $('template-body'),
    reportCanvas: $<HTMLCanvasElement>('report-canvas'),
    cmdBlock: $('cmd-block'),
    ollamaBlock: $('ollama-block'),
    resultActions: $('result-actions'),
    copyCmdBtn: $<HTMLButtonElement>('copy-cmd'),
    copyOllamaBtn: $<HTMLButtonElement>('copy-ollama'),
    dlJsonBtn: $<HTMLButtonElement>('dl-json'),
    dlPngBtn: $<HTMLButtonElement>('dl-png'),
    dlTemplateBtn: $<HTMLButtonElement>('dl-template'),
    sharePngBtn: $<HTMLButtonElement>('share-png'),
    drawer: $('drawer'),
    drawerBody: $('drawer-body'),
    drawerToggle: $<HTMLButtonElement>('drawer-toggle'),
    scrim: $('scrim'),
    banner: $('trust-banner'),
  };

  wireModals(root);
  initGlossary(root);
  return refs;
}

// ── Modals ───────────────────────────────────────────────────────────

const MODALS: Record<string, { title: string; body: string }> = {
  how: {
    title: 'How it works',
    body: `
      <ol class="steps">
        <li><strong>It asks for the first few megabytes — and only those.</strong> A
          <span data-term="GGUF">GGUF</span> file puts all of its metadata and its complete
          <span data-term="tensor">tensor</span> table at the <em>front</em>, before a single weight.
          GGUFScope sends an <span data-term="HTTP Range">HTTP Range</span> request for roughly
          8 MB and stops there. On a 5 GB model that is about 0.1% of the file.</li>
        <li><strong>It reads the real table, not the filename.</strong> Every tensor's true
          <span data-term="quantisation">quantisation</span> type and shape are in that table.
          This matters because <code>Q4_K_M</code> is a <span data-term="mixture">mixture</span>,
          not a type — the file holds Q4_K, Q5_K and Q6_K tensors side by side. Multiplying a
          parameter count by "4 bits" is simply the wrong sum.</li>
        <li><strong>It sizes the weights exactly.</strong> Each ggml type has a known
          <span data-term="block size">block size</span> — Q4_K packs 256 weights into 144 bytes —
          so the bytes for every tensor are arithmetic, not estimation.</li>
        <li><strong>It sizes the <span data-term="KV cache">KV cache</span> properly.</strong> The
          cache is computed per layer from the model's own <code>head_count_kv</code>,
          <code>key_length</code> and <code>value_length</code>, so
          <span data-term="GQA">grouped-query attention</span> is accounted for rather than
          assumed away. Change the context or the
          <span data-term="cache type">cache type</span> and the number moves with it.</li>
        <li><strong>You leave with something to run.</strong> A verdict against your card with
          every line item shown, a solved <code>llama-server</code> command, an Ollama
          <code>Modelfile</code>, the model's <span data-term="chat template">chat template</span>,
          a JSON report and a shareable card.</li>
      </ol>
      <p class="modal-note">Everything above happens in this tab. There is no server: the Range
        request goes straight from your browser to Hugging Face's CDN, which is the only reason
        this can be a static page at all.</p>`,
  },
  privacy: {
    title: 'Privacy &amp; threat model',
    body: `
      <h3>Protected</h3>
      <ul>
        <li><strong>A local <code>.gguf</code> never leaves your device.</strong> It is read with
          <code>Blob.slice</code> in this tab — only the header, and only into memory. Nothing is
          uploaded, and this path works with your network off.</li>
        <li>Your GPU size, context choice and every computed number stay here. All artefacts — the
          JSON, the PNG, the Modelfile, the <code>.jinja</code> — are generated on your device.</li>
        <li>No account, no cookies, no fingerprinting, no third-party fonts. Preferences are kept in
          <code>localStorage</code> on this device only.</li>
      </ul>
      <h3>Not protected</h3>
      <ul>
        <li><strong>Inspecting a remote model is a request to Hugging Face.</strong> There is no way
          around that — huggingface.co and its CDN see your IP and which model file you asked about,
          exactly as if you had opened the model page. If that matters for a particular model,
          download it yourself and use the local path instead.</li>
        <li><strong>Typing in the model box searches Hugging Face as you type.</strong> A moment
          after you stop typing, whatever is in the field goes to their public model-search API — so
          they see partial queries before you commit to anything. A full URL or a <code>.gguf</code>
          address is never searched, because there is nothing left to suggest: paste one of those and
          the search is skipped entirely.</li>
        <li>Loading this page is an ordinary web request: GitHub Pages, the host, sees your IP and
          browser as any website does.</li>
        <li>The numbers are a careful estimate of what llama.cpp will allocate, not a promise. Your
          driver, build flags and other running processes all take VRAM too — which is why the
          verdict shows a headroom band rather than a bare yes.</li>
      </ul>
      <h3>Trust surface</h3>
      <ul>
        <li>The static site bundle, hash-pinned by the GitHub Pages deploy, and the TLS connection
          to it.</li>
        <li><code>huggingface.co</code> and <code>*.hf.co</code> — contacted only when you inspect a
          remote model, never for a local file.</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no
          fingerprinting, no cross-site tracking; your files and data are never sent to it.</li>
        <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent
          unless you open the feedback form and press Send; your files and data never are.</li>
      </ul>`,
  },
  about: {
    title: 'About GGUFScope',
    body: `
      <p>GGUFScope answers one question properly: <em>will this model actually fit on my card?</em>
        It reads the model's real tensor table over an <span data-term="HTTP Range">HTTP Range</span>
        request — a few megabytes of a file that may be twenty gigabytes — and does the same
        arithmetic llama.cpp does when it loads.</p>
      <p>The VRAM calculators that rank for this question guess from a parameter count and a quant
        name. Both inputs are unreliable: <code>Q4_K_M</code> is a
        <span data-term="mixture">mixture</span> of several types, and the
        <span data-term="KV cache">KV cache</span> depends on
        <span data-term="GQA">GQA</span> geometry that a parameter count cannot tell you. Reading
        the file is the only way to be right, and it turns out a browser can do it.</p>
      <p>Built with a first-party GGUF parser, no backend, and no upload. Part of a small workshop
        of privacy-first browser tools.</p>
      <p class="modal-links">
        <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
        <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>
        <a href="https://github.com/ben-gy/ggufscope" target="_blank" rel="noopener">source</a>
      </p>`,
  },
};

let onModalOpen: ((id: string) => void) | null = null;
export function setModalOpenHook(fn: (id: string) => void): void {
  onModalOpen = fn;
}

function wireModals(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => openModal(root, btn.dataset.modal!));
  });
  root.querySelector('#trust-banner')?.addEventListener('click', () => openModal(root, 'privacy'));
}

export function openModal(root: HTMLElement, id: string): void {
  const def = MODALS[id];
  if (!def) return;
  const host = root.querySelector<HTMLElement>('#modal-host')!;
  const scrim = root.querySelector<HTMLElement>('#scrim')!;
  host.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${stripTags(def.title)}">
      <div class="modal-head">
        <h2>${def.title}</h2>
        <button type="button" class="modal-close" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">${def.body}</div>
    </div>`;
  host.hidden = false;
  scrim.hidden = false;
  initGlossary(host);
  const close = () => closeModal(root);
  host.querySelector('.modal-close')?.addEventListener('click', close);
  host.querySelector('.modal')?.addEventListener('click', (e) => e.stopPropagation());
  host.addEventListener('click', close, { once: true });
  onModalOpen?.(id);
}

export function closeModal(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>('#modal-host')!;
  const scrim = root.querySelector<HTMLElement>('#scrim')!;
  host.hidden = true;
  host.innerHTML = '';
  if (root.querySelector<HTMLElement>('#drawer')!.hidden) scrim.hidden = true;
}

export function isModalOpen(root: HTMLElement): boolean {
  return !root.querySelector<HTMLElement>('#modal-host')!.hidden;
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&');
}

// ── Glossary tooltip ─────────────────────────────────────────────────

export function initGlossary(scope: HTMLElement): void {
  scope.querySelectorAll<HTMLElement>('[data-term]').forEach((el) => {
    if (el.dataset.glossReady) return;
    const term = el.dataset.term!;
    if (!GLOSSARY[term]) return;
    el.dataset.glossReady = '1';
    el.classList.add('glossary-link');
    el.tabIndex = 0;
    el.setAttribute('role', 'button');
    const show = (e: Event) => {
      e.stopPropagation();
      showGlossary(el, GLOSSARY[term]);
    };
    el.addEventListener('click', show);
    el.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') show(e);
    });
  });
}

function showGlossary(anchor: HTMLElement, text: string): void {
  const tip = document.querySelector<HTMLElement>('#glossary-tip')!;
  tip.textContent = text;
  tip.hidden = false;
  const r = anchor.getBoundingClientRect();
  const tw = Math.min(340, window.innerWidth - 24);
  tip.style.width = `${tw}px`;
  let left = r.left + r.width / 2 - tw / 2;
  left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
  tip.style.left = `${left}px`;
  // Flip above the anchor when there is no room below.
  const belowRoom = window.innerHeight - r.bottom;
  tip.style.top = belowRoom < 160 ? '' : `${r.bottom + 8}px`;
  tip.style.bottom = belowRoom < 160 ? `${window.innerHeight - r.top + 8}px` : '';
  const dismiss = (e: Event) => {
    if (e.target !== anchor) {
      tip.hidden = true;
      document.removeEventListener('click', dismiss, true);
    }
  };
  setTimeout(() => document.addEventListener('click', dismiss, true), 0);
}

export function hideGlossary(): void {
  const tip = document.querySelector<HTMLElement>('#glossary-tip');
  if (tip) tip.hidden = true;
}

// ── Toast ────────────────────────────────────────────────────────────

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function showToast(message: string): void {
  const toast = document.querySelector<HTMLElement>('#toast');
  if (!toast) return;
  toast.textContent = message;
  toast.hidden = false;
  toast.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => (toast.hidden = true), 250);
  }, 2600);
}
